import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import LoadingSkeleton from '../components/LoadingSkeleton';
import { Calendar as CalendarIcon, Users, Settings, BookOpen, UserCog, Mail, ChevronRight, Clock, User, Eye, Database, Phone, Monitor, MapPin, FileText, NotebookPen } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_EMAILS = ["julie@jcp-teaching.com", "test@example.com", "jc-p@outlook.de"];

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, loading: authLoading, token } = useAuth();
  const [weeklyBookings, setWeeklyBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [sendingReminder, setSendingReminder] = useState({});
  const [pendingUsersCount, setPendingUsersCount] = useState(0);
  const [pendingWeekendCoursesCount, setPendingWeekendCoursesCount] = useState(0);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    
    if (user && !ADMIN_EMAILS.includes(user.email)) {
      toast.error('Admin access required');
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && ADMIN_EMAILS.includes(user.email) && token) {
      fetchWeeklyBookings();
      fetchPendingCounts();
    }
  }, [user, token]);

  const fetchWeeklyBookings = async () => {
    try {
      const response = await axios.get(`${API}/admin/bookings/weekly`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeeklyBookings(response.data);
    } catch (error) {
      console.error('Error fetching weekly bookings:', error);
    } finally {
      setLoadingBookings(false);
    }
  };

  const fetchPendingCounts = async () => {
    try {
      // Fetch pending users count
      const usersResponse = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pendingUsers = usersResponse.data.filter(u => u.approved === false);
      setPendingUsersCount(pendingUsers.length);

      // Fetch pending weekend courses count
      const coursesResponse = await axios.get(`${API}/admin/weekend-courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pendingCourses = coursesResponse.data.filter(c => c.status === 'requested');
      setPendingWeekendCoursesCount(pendingCourses.length);
    } catch (error) {
      console.error('Error fetching pending counts:', error);
    }
  };

  const sendReminder = async (bookingId, clientEmail) => {
    setSendingReminder(prev => ({ ...prev, [bookingId]: true }));
    try {
      await axios.post(`${API}/admin/bookings/${bookingId}/send-reminder`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${t('Reminder sent to')} ${clientEmail}`);
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error(error.response?.data?.detail || t('Could not send reminder'));
    } finally {
      setSendingReminder(prev => ({ ...prev, [bookingId]: false }));
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.getTime() === today.getTime()) {
      return t('Today');
    } else if (date.getTime() === tomorrow.getTime()) {
      return t('Tomorrow');
    }
    
    return date.toLocaleDateString('da-DK', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const groupBookingsByDate = (bookings) => {
    const grouped = {};
    bookings.forEach(booking => {
      if (!grouped[booking.date]) {
        grouped[booking.date] = [];
      }
      grouped[booking.date].push(booking);
    });
    return grouped;
  };

  if (authLoading || !user) {
    return (
      <div>
        <Navbar />
        <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
          <LoadingSkeleton type="card" count={3} />
        </div>
      </div>
    );
  }

  if (!ADMIN_EMAILS.includes(user.email)) {
    return null;
  }

  const adminSections = [
    {
      title: 'Noter',
      icon: NotebookPen,
      path: '/admin/noter',
      color: '#6b5e8e',
      gradient: 'linear-gradient(135deg, #6b5e8e 0%, #8a7aad 100%)',
      badge: 0
    },
    {
      title: t('Calendar'),
      icon: CalendarIcon,
      path: '/admin/calendar',
      color: '#7c6f5e',
      gradient: 'linear-gradient(135deg, #7c6f5e 0%, #9d8b7a 100%)',
      badge: 0
    },
    {
      title: t('Plan'),
      icon: Settings,
      path: '/admin/availability',
      color: '#6b8e6f',
      gradient: 'linear-gradient(135deg, #6b8e6f 0%, #8aad8e 100%)',
      badge: 0
    },
    {
      title: t('Weekend Courses'),
      icon: CalendarIcon,
      path: '/admin/weekend-courses',
      color: '#9e7a5a',
      gradient: 'linear-gradient(135deg, #9e7a5a 0%, #b99578 100%)',
      badge: pendingWeekendCoursesCount
    },
    {
      title: t('Bookings'),
      icon: BookOpen,
      path: '/admin/bookings',
      color: '#5a8b9e',
      gradient: 'linear-gradient(135deg, #5a8b9e 0%, #7aa5b5 100%)',
      badge: 0
    },
    {
      title: t('User Management'),
      icon: UserCog,
      path: '/admin/users',
      color: '#8b6b9e',
      gradient: 'linear-gradient(135deg, #8b6b9e 0%, #a68ab5 100%)',
      badge: pendingUsersCount
    },
    {
      title: t('Service Visibility'),
      icon: Eye,
      path: '/admin/services',
      color: '#5e7c6f',
      gradient: 'linear-gradient(135deg, #5e7c6f 0%, #7a9d8b 100%)',
      badge: 0
    },
    {
      title: t('Backup'),
      icon: Database,
      path: '/admin/backup',
      color: '#8b7c5e',
      gradient: 'linear-gradient(135deg, #8b7c5e 0%, #a59578 100%)',
      badge: 0
    },
    {
      title: 'JCP',
      icon: FileText,
      path: '/admin/jcp',
      color: '#5e8b7c',
      gradient: 'linear-gradient(135deg, #5e8b7c 0%, #78a595 100%)',
      badge: 0
    }
  ];

  const groupedBookings = groupBookingsByDate(weeklyBookings);

  return (
    <div>
      <Navbar />
      <div className="booking-page" style={{ paddingTop: '100px', minHeight: '100vh', background: '#fafafa' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem' }}>
          {/* Header */}
          <div style={{ 
            background: 'white', 
            padding: '2rem', 
            borderRadius: '12px', 
            marginBottom: '2rem',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            <h1 className="booking-title" style={{ marginBottom: '0.5rem', fontSize: '2rem' }}>
              {t('Admin Dashboard')}
            </h1>
            <p style={{ color: '#666', marginBottom: '0', fontSize: '1rem' }}>{t('Select an area to manage')}</p>
          </div>

          {/* Admin Section Boxes */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem'
          }}>
            {adminSections.map((section) => {
              const Icon = section.icon;
              return (
                <div
                  key={section.path}
                  onClick={() => navigate(section.path)}
                  data-testid={`admin-section-${section.title.toLowerCase().replace(' ', '-')}`}
                  style={{
                    background: section.gradient,
                    padding: '1.5rem',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    color: 'white',
                    minHeight: '100px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                  }}
                >
                  {/* Notification Badge */}
                  {section.badge > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '10px',
                      left: '10px',
                      background: '#ff5722',
                      color: 'white',
                      borderRadius: '50%',
                      width: '26px',
                      height: '26px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      boxShadow: '0 2px 8px rgba(255, 87, 34, 0.5)',
                      border: '2px solid white',
                      zIndex: 10
                    }}>
                      {section.badge > 9 ? '9+' : section.badge}
                    </div>
                  )}
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    position: 'relative',
                    zIndex: 1
                  }}>
                    <Icon size={28} />
                    <h2 style={{ 
                      fontSize: '1.3rem',
                      fontWeight: 600,
                      margin: 0,
                      color: 'white'
                    }}>
                      {section.title}
                    </h2>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Weekly Bookings Overview */}
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '12px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h2 style={{ margin: 0, fontSize: '1.4rem', color: '#333' }}>
                {t('Upcoming Bookings (7 days)')}
              </h2>
              <span style={{ 
                background: '#7c6f5e', 
                color: 'white', 
                padding: '0.25rem 0.75rem', 
                borderRadius: '20px',
                fontSize: '0.9rem'
              }}>
                {weeklyBookings.length} {t('bookings')}
              </span>
            </div>

            {loadingBookings ? (
              <LoadingSkeleton type="list" count={3} />
            ) : weeklyBookings.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '3rem', 
                color: '#999',
                background: '#f9f9f9',
                borderRadius: '8px'
              }}>
                <CalendarIcon size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                <p style={{ margin: 0 }}>{t('No bookings in the next 7 days')}</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {Object.entries(groupedBookings).map(([date, bookings]) => (
                  <div key={date}>
                    <div style={{ 
                      fontSize: '0.9rem', 
                      fontWeight: 600, 
                      color: '#7c6f5e',
                      marginBottom: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <CalendarIcon size={16} />
                      {formatDate(date)}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {bookings.map((booking) => {
                        const isOnline = booking.booking_type === 'online';
                        return (
                          <div
                            key={booking.id}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              padding: '0.875rem',
                              background: '#fafafa',
                              borderRadius: '8px',
                              borderLeft: `4px solid ${isOnline ? '#2196f3' : '#7c6f5e'}`
                            }}
                          >
                            {/* Top row: Time, Type badge */}
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              marginBottom: '0.5rem',
                              flexWrap: 'wrap',
                              gap: '0.5rem'
                            }}>
                              <span style={{ 
                                color: '#555', 
                                fontWeight: 600,
                                background: '#fff',
                                padding: '0.15rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.9rem'
                              }}>
                                {booking.start_time}
                              </span>
                              <span style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                padding: '0.25rem 0.6rem',
                                background: isOnline ? '#e3f2fd' : '#e8f5e9',
                                color: isOnline ? '#1976d2' : '#2e7d32',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: 600
                              }}>
                                {isOnline ? <Monitor size={14} /> : <MapPin size={14} />}
                                {isOnline ? 'Online' : 'Heiden'}
                              </span>
                            </div>
                            
                            {/* Client info row */}
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: '0.5rem'
                            }}>
                              <div>
                                <div style={{ fontWeight: 600, color: '#333', fontSize: '0.95rem' }}>
                                  {booking.client_name}
                                </div>
                                {booking.client_phone && (
                                  <div style={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    marginTop: '0.25rem'
                                  }}>
                                    <a 
                                      href={`tel:${booking.client_phone}`}
                                      style={{ 
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '0.25rem',
                                        color: '#1976d2',
                                        fontSize: '0.85rem',
                                        textDecoration: 'none'
                                      }}
                                    >
                                      <Phone size={14} />
                                      {booking.client_phone}
                                    </a>
                                    <a 
                                      href={`https://wa.me/${booking.client_phone.replace(/[^0-9]/g, '')}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ 
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0.25rem 0.5rem',
                                        background: '#25D366',
                                        color: 'white',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        borderRadius: '4px',
                                        textDecoration: 'none'
                                      }}
                                    >
                                      WhatsApp
                                    </a>
                                  </div>
                                )}
                              </div>
                              
                              {/* Actions */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <button
                                  onClick={() => sendReminder(booking.id, booking.client_email)}
                                  disabled={sendingReminder[booking.id]}
                                  data-testid={`send-reminder-${booking.id}`}
                                  title={t('Send Reminder')}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.3rem',
                                    padding: '0.4rem 0.6rem',
                                    background: sendingReminder[booking.id] ? '#ccc' : '#5a8b9e',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: sendingReminder[booking.id] ? 'not-allowed' : 'pointer',
                                    fontSize: '0.8rem',
                                    fontWeight: 500
                                  }}
                                >
                                  <Mail size={14} />
                                  {sendingReminder[booking.id] ? '...' : t('Reminder')}
                                </button>
                                <button
                                  onClick={() => navigate(`/admin/bookings`)}
                                  title={t('View All Bookings')}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '0.4rem',
                                    background: 'transparent',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <ChevronRight size={16} />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
